<script lang="ts">
import { computed, defineComponent, onMounted, ref, Ref } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { router, useRoute } from '../../router'
import { useI18n } from '../../setup/setup-i18n'
import { ResetPasswordState, User } from '../../types'

const { useState: useUserStoreState, useActions: useUserStoreActions } = createNamespacedHelpers('User')

export default defineComponent({
  name: 'ResetPasswordPage',

  components: {
    DefaultLayout,
  },

  setup() {

    const loading = ref(false)
    const errorMessage = ref(null)

    // these are template refs which can be referenced in the template
    const form = ref(null)
    const email = ref(null)

    const currentRoute = useRoute()

    const tokenExpired = computed(() => !!currentRoute.value.query.tokenExpired)

    const { t } = useI18n()

    const rules = {
      email: [
        { required: true, message: t('ResetPasswordPage.errorFieldRequired') },
        { type: 'email', message: t('ResetPasswordPage.errorInvalidEmail'), trigger: 'blur' },
      ],
    }

    const user = useUserStoreState(['user']).user as Ref<User>
    const { requestResetPassword } = useUserStoreActions(['requestResetPassword'])

    const handleSubmit = () => {

      return form.value?.validate().then(async (valid) => {

        if (!valid) {
          return
        }

        loading.value = true

        try {

          const state: ResetPasswordState = await requestResetPassword(user.value.email)

          if (state === ResetPasswordState.ACTIVATE) {
            // The user's account is not yet activated.
            // An activation email has been sent (instead of a password reset email)
            router.push({ name: 'ActivateSentPage' })
          } else {
            // The reset password email has been sent
            router.push({ name: 'ResetPasswordSentPage' })
          }

        } catch (error) {

          if (error.request && !error.response) {
            errorMessage.value = t('ResetPasswordPage.errorNetwork') as string
            return
          }

          router.replace({ name: 'UnknownErrorPage' })

        } finally {
          loading.value = false
        }

      })

    }

    const captureCachedEmail = () => {
      user.value.email = currentRoute.value.query.email as string || user.value.email
    }

    const focusOnEmail = () => {
      email.value.select()
    }

    // to be called before onMounted and around same time as created lifecycle hook
    captureCachedEmail()

    if (tokenExpired.value) {
      errorMessage.value = t('ResetPasswordPage.errorTokenExpired') as string
    }

    onMounted(() => {
      focusOnEmail()
    })

    return {
      t,
      user,
      rules,
      handleSubmit,
      loading,
      errorMessage,
      email,
      form,
    }

  },

})

</script>

<template>
  <default-layout
    :title="t('ResetPasswordPage.pageTitle')"
    :sub-title="t('ResetPasswordPage.subTitle')"
  >
    <sm-form
      ref="form"
      class="reset-form"
      @submit.native.prevent="handleSubmit"
    >
      <fieldset>
        <sm-input
          ref="email"
          v-model="user.email"
          type="email"
          name="email"
          autofocus
          autocomplete="username"
          rules="required|email"
          mode="lazy"
          :label="$t('ResetPasswordPage.emailLabel')"
          :placeholder="t('ResetPasswordPage.emailPlaceholder')"
        />
        <sm-button
          native-type="submit"
          type="primary"
          class="block-button"
          :loading="loading"
        >
          {{ t('ResetPasswordPage.submitButton') }}
        </sm-button>

        <div class="text-center">
          <router-link
            class="link-button"
            :to="{ name: 'LoginPage' }"
          >
            {{ t('ResetPasswordPage.backToLoginLink') }}
          </router-link>
        </div>

        <sm-help-card
          v-if="errorMessage"
          type="warning"
        >
          <template #header>
            {{ errorMessage }}
          </template>
        </sm-help-card>
      </fieldset>
    </sm-form>
  </default-layout>
</template>

<style lang="scss" scoped>

.reset-form .block-button {
  @apply w-full;
  margin-bottom: 22px;
}

::v-deep .reset-form .block-button .sm-button__content {
  @apply block;
}

.reset-form .link-button {
  @apply text-sm;
}

::v-deep .sm-form {
  @apply flex justify-center flex-col;
  fieldset {
    @apply self-center sm:w-[364px] max-w-[364px] h-[300px];
    min-width: inherit;
  }
  @media only screen and (max-width: 639px) {
    fieldset {
      width: -webkit-fill-available;
      width: -moz-available;
      min-width: 280px;
    }
  }
}

</style>
